
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import {
  Download, Upload, Check, Trash2, ShieldCheck,
  Rocket, RefreshCw, Layers, Lock, Unlock,
  ExternalLink, Globe, Cloud, Activity,
  Wifi, Server, AlertCircle, GitMerge, Sparkles
} from 'lucide-react';
import { UserSettings } from '../types.ts';
import { useToast } from '../hooks/useToast.ts';
import { RoleSelector } from '../components/RoleSelector.tsx';
import { useRole } from '../hooks/useRole.ts';

export const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const settingsList = useStore(() => api.settings.list(), [], 'settings');
  const user = settingsList?.[0];
  const [data, setData] = useState<Partial<UserSettings>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'profile' | 'preflight' | 'diagnostic' | 'intelligence'>('profile');
  const [isSyncing, setIsSyncing] = useState(false);

  const [provider, setProvider] = useState<'local' | 'cloudflare'>(
    (localStorage.getItem('lc_provider') as any) || 'local'
  );
  const { isOwner } = useRole();

  const [prodLock, setProdLock] = useState(true);

  useEffect(() => { if (user) setData(user); }, [user]);

  const save = async (update: Partial<UserSettings>) => {
    setData({ ...data, ...update });
    if (user) await api.settings.update(user.id!, update);
    else await api.settings.add({
      userName: update.userName || t('common.operator'),
      userTitle: update.userTitle || t('common.operations_control'),
      organizationName: update.organizationName || '',
      theme: update.theme || 'zen',
      role: 'PM'
    });
  };

  const handleProviderSwitch = (p: 'local' | 'cloudflare') => {
    setProvider(p);
    localStorage.setItem('lc_provider', p);
    addToast({
      type: 'info',
      title: t('common.provider_switched'),
      message: t('common.provider_switched_msg', { p: p.toUpperCase() })
    });
    setTimeout(() => window.location.reload(), 1500);
  };

  const handleLanguageChange = (lng: string) => {
    i18n.changeLanguage(lng);
    addToast({
      type: 'success',
      title: t('common.language_changed', { defaultValue: 'Language Updated' }),
      message: t('common.language_changed_msg', {
        lng: lng === 'ko' ? t('common.korean') : t('common.english'),
        defaultValue: `System interface updated to ${lng === 'ko' ? 'Korean' : 'English'}.`
      })
    });
  };

  const exportData = async () => {
    const stores = [
      'projects', 'issues', 'evm', 'risks', 'budgetItems',
      'reports', 'documents', 'settings', 'tasks',
      'checklistItems', 'proposals', 'changeOrders',
      'complianceRequirements', 'subcontractors', 'wbsTasks', 'projectLogs'
    ];
    const dump: any = {};
    for (const store of stores) { dump[store] = await (api as any)[store].list(); }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(dump)], { type: 'application/json' }));
    a.download = `LeanControls_Pro_Vault_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    save({ lastBackupAt: new Date().toISOString() });
    addToast({ type: 'success', title: t('common.vault_exported'), message: t('common.vault_exported_msg') });
  };

  const clearAll = async () => {
    if (prodLock) {
      addToast({ type: 'error', title: t('common.action_blocked'), message: t('common.release_lock_msg') });
      return;
    }
    if (!window.confirm("CRITICAL: This will permanently destroy all local project data. Proceed?")) return;

    const stores = [
      'projects', 'issues', 'evm', 'risks', 'budgetItems',
      'reports', 'documents', 'settings', 'tasks',
      'checklistItems', 'proposals', 'changeOrders',
      'complianceRequirements', 'subcontractors', 'wbsTasks', 'projectLogs'
    ];

    for (const store of stores) {
      if ((api as any)[store]?.clear) {
        await (api as any)[store].clear();
      }
    }

    addToast({
      type: 'error',
      title: t('common.registry_wiped'),
      message: t('common.registry_wiped_msg')
    });

    setTimeout(() => window.location.reload(), 1000);
  };

  const runInfrastructureTest = () => {
    setIsSyncing(true);
    setTimeout(() => {
      setIsSyncing(false);
      addToast({
        type: 'info',
        title: `${provider.toUpperCase()} Edge Ping`,
        message: t('common.communication_established', { defaultValue: 'Baseline communication with Cloudflare Global Network established.' })
      });
    }, 1500);
  }

  const infraChecklist = [
    { id: 'local', title: "Local Vault (IndexedDB)", desc: "Zero-latency, offline-first storage for site offices.", link: "#" },
    { id: 'cloudflare', title: "Cloudflare (D1 SQL)", desc: "Relational SQL at the Edge. Optimal for multi-region financial audit.", link: "https://developers.cloudflare.com/d1/" }
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-12 animate-in fade-in pb-32">
      <div className="space-y-4">
        <h1 className="text-5xl font-black text-black tracking-tighter uppercase">{t('settings.system_console')}</h1>
        <p className="text-zinc-500 font-bold text-sm">{t('settings.console_desc')}</p>
      </div>

      <div className="flex gap-2 p-1.5 bg-zinc-100 rounded-2xl w-fit overflow-x-auto no-scrollbar">
        <button onClick={() => setActiveTab('profile')} className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all whitespace-nowrap ${activeTab === 'profile' ? 'bg-white text-black shadow-md' : 'text-zinc-400'}`}>{t('settings.profile')}</button>
        <button onClick={() => setActiveTab('preflight')} className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all whitespace-nowrap ${activeTab === 'preflight' ? 'bg-white text-black shadow-md' : 'text-zinc-400'} flex items-center gap-2`}>
          <GitMerge size={14} /> {t('settings.stack_logic')}
        </button>
        <button onClick={() => setActiveTab('diagnostic')} className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all whitespace-nowrap ${activeTab === 'diagnostic' ? 'bg-white text-black shadow-md' : 'text-zinc-400'} flex items-center gap-2`}>
          <Activity size={14} /> {t('settings.diagnostic')}
        </button>
        <button onClick={() => setActiveTab('intelligence')} className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all whitespace-nowrap ${activeTab === 'intelligence' ? 'bg-white text-black shadow-md' : 'text-zinc-400'} flex items-center gap-2`}>
          <Sparkles size={14} /> Intelligence
        </button>
      </div>

      {activeTab === 'profile' && (
        <div className="space-y-12 animate-in fade-in">
          <section className="bg-white p-10 rounded-[3rem] border border-zinc-200 space-y-8 shadow-sm">
            <h3 className="font-black text-black text-2xl tracking-tight uppercase">{t('settings.identity_profile')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block">{t('settings.operator_name')}</label>
                <input className="w-full border border-zinc-100 bg-zinc-50 p-5 rounded-2xl text-black font-black focus:bg-white outline-none transition-all" value={data.userName || ''} onChange={e => save({ userName: e.target.value })} placeholder={t('settings.operator_name')} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block">{t('settings.organization')}</label>
                <input className="w-full border border-zinc-100 bg-zinc-50 p-5 rounded-2xl text-black font-black focus:bg-white outline-none transition-all" value={data.organizationName || ''} onChange={e => save({ organizationName: e.target.value })} placeholder={t('settings.organization')} />
              </div>
            </div>
          </section>

          <section className="bg-white p-10 rounded-[3rem] border border-zinc-200 space-y-8 shadow-sm">
            <h3 className="font-black text-black text-2xl tracking-tight uppercase">{t('settings.regional_config')}</h3>
            <div className="space-y-4">
              <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block">{t('settings.interface_language')}</label>
              <div className="flex gap-4">
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`flex-1 py-4 px-6 rounded-2xl font-black text-xs uppercase tracking-widest transition-all border-2 ${i18n.language === 'en' ? 'bg-black text-white border-black shadow-lg scale-105' : 'bg-zinc-50 text-zinc-400 border-zinc-100 hover:border-black hover:text-black'}`}
                >
                  {t('common.english')}
                </button>
                <button
                  onClick={() => handleLanguageChange('ko')}
                  className={`flex-1 py-4 px-6 rounded-2xl font-black text-xs uppercase tracking-widest transition-all border-2 ${i18n.language === 'ko' ? 'bg-black text-white border-black shadow-lg scale-105' : 'bg-zinc-50 text-zinc-400 border-zinc-100 hover:border-black hover:text-black'}`}
                >
                  {t('common.korean')}
                </button>
              </div>
            </div>
          </section>

          <RoleSelector />

          {isOwner && (
            <section className="bg-black p-10 rounded-[3.5rem] shadow-2xl text-white space-y-8">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-zinc-800 rounded-2xl text-emerald-400"><ShieldCheck size={24} /></div>
                  <div>
                    <h3 className="font-black text-2xl tracking-tight leading-none uppercase">{t('settings.security_ledger')}</h3>
                    <p className="text-zinc-500 text-xs mt-2 font-bold uppercase tracking-widest">{t('settings.ledger_desc')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setProdLock(!prodLock)}
                  className={`flex items-center gap-3 px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${prodLock ? 'bg-zinc-900 border-zinc-800 text-zinc-500' : 'bg-rose-500/10 border-rose-500/30 text-rose-500'}`}
                >
                  {prodLock ? <Lock size={14} /> : <Unlock size={14} />} {prodLock ? t('settings.locked') : t('settings.unlocked')}
                </button>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <button onClick={exportData} className="flex-1 bg-white text-black py-5 rounded-[2rem] flex justify-center items-center gap-3 text-[10px] font-black uppercase tracking-widest transition-all hover:scale-[1.03]">
                  <Download size={20} /> {t('settings.download_vault')}
                </button>
                <button onClick={() => fileRef.current?.click()} className="flex-1 bg-zinc-900 text-zinc-300 border border-zinc-800 py-5 rounded-[2rem] flex justify-center items-center gap-3 text-[10px] font-black uppercase tracking-widest hover:text-white transition-all">
                  <Upload size={20} /> {t('settings.restore')}
                </button>
                <input type="file" ref={fileRef} className="hidden" />
              </div>
              <div className="pt-8 border-t border-zinc-900 flex justify-center">
                <button
                  onClick={clearAll}
                  disabled={prodLock}
                  className={`text-[11px] font-black uppercase tracking-[0.25em] py-2 flex items-center gap-3 transition-colors ${prodLock ? 'text-zinc-800 opacity-20 cursor-not-allowed' : 'text-zinc-600 hover:text-rose-500'}`}
                >
                  <Trash2 size={16} /> {t('settings.wipe_records')}
                </button>
              </div>
            </section>
          )}
        </div >
      )}

      {
        activeTab === 'preflight' && (
          <section className="bg-white p-10 rounded-[3rem] border border-zinc-200 shadow-sm animate-in slide-in-from-right-4">
            <div className="flex items-center gap-4 mb-10">
              <div className="p-3 bg-black rounded-2xl text-white shadow-xl"><Layers size={24} /></div>
              <div>
                <h3 className="text-2xl font-black text-black tracking-tight uppercase">{t('settings.cloud_architecture')}</h3>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mt-1">{t('settings.managed_via')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-12">
              {infraChecklist.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleProviderSwitch(item.id as any)}
                  className={`flex items-start gap-6 p-8 rounded-[2.5rem] border text-left transition-all group ${provider === item.id ? 'border-black bg-zinc-50 ring-2 ring-black shadow-xl scale-[1.02]' : 'border-zinc-100 bg-zinc-50/30 hover:bg-white'}`}
                >
                  <div className={`p-4 rounded-2xl border transition-all ${provider === item.id ? 'bg-black text-white border-black' : 'bg-white text-zinc-300 border-zinc-100 group-hover:text-black group-hover:border-black'}`}>
                    {item.id === 'local' ? <Server size={24} /> : <Globe size={24} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="font-black text-lg text-black">{item.title}</h4>
                      <Check size={20} className={provider === item.id ? 'text-black' : 'text-transparent'} />
                    </div>
                    <p className="text-xs text-zinc-500 font-bold leading-relaxed mb-4">{item.desc}</p>
                    <a href={item.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[9px] font-black uppercase text-zinc-400 hover:text-black tracking-widest flex items-center gap-2">
                      System Documentation <ExternalLink size={10} />
                    </a>
                  </div>
                </button>
              ))}
            </div>

            <div className="p-8 bg-zinc-900 rounded-[2.5rem] border border-zinc-800 flex items-center justify-between">
              <div className="space-y-1">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-400">{t('settings.pipeline_status')}</h4>
                <p className="text-xs text-zinc-500 font-bold">{t('settings.pipeline_success')}</p>
              </div>
              <button
                onClick={runInfrastructureTest}
                disabled={isSyncing}
                className="px-8 py-4 bg-white text-black text-[10px] font-black uppercase tracking-widest rounded-2xl hover:scale-105 transition-all flex items-center gap-3 shadow-xl"
              >
                {isSyncing ? <RefreshCw size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {t('settings.verify_node')}
              </button>
            </div>
          </section>
        )
      }

      {
        activeTab === 'diagnostic' && (
          <section className="bg-white p-10 rounded-[3rem] border border-zinc-200 shadow-sm animate-in slide-in-from-bottom-4">
            <div className="flex items-center gap-4 mb-10">
              <div className="p-3 bg-indigo-600 rounded-2xl text-white shadow-xl"><Activity size={24} /></div>
              <div>
                <h3 className="text-2xl font-black text-black tracking-tight uppercase">{t('settings.edge_diagnostics')}</h3>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mt-1">{t('settings.global_health')}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-6 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Wifi size={20} className="text-emerald-500" />
                  <div>
                    <div className="text-xs font-black text-black uppercase">Edge Connectivity</div>
                    <div className="text-[10px] font-bold text-zinc-400 uppercase">Cloudflare Anycast Active</div>
                  </div>
                </div>
                <span className="px-3 py-1 bg-emerald-500 text-white text-[8px] font-black uppercase rounded-lg">Optimized</span>
              </div>

              <div className="p-6 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Server size={20} className="text-black" />
                  <div>
                    <div className="text-xs font-black text-black uppercase">D1 SQL Health</div>
                    <div className="text-[10px] font-bold text-zinc-400 uppercase">Ready for queries</div>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-zinc-900 rounded-[2rem] text-white">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle size={18} className="text-amber-400" />
                  <h4 className="text-[10px] font-black uppercase tracking-widest">System Actions</h4>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed font-medium mb-6">
                  Manage the demo data and refresh the browser instance.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      localStorage.removeItem('lc_seeded');
                      addToast({ type: 'info', title: 'Demo Reset', message: 'Demo data will reload on next refresh.' });
                      setTimeout(() => window.location.reload(), 1000);
                    }}
                    className="w-full py-4 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-500/30 transition-all"
                  >
                    Reset Demo Data
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full py-4 bg-white/10 border border-white/10 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-white/20 transition-all"
                  >
                    Clear Browser Instance
                  </button>
                </div>
              </div>
            </div>
          </section>
        )
      }
      {
        activeTab === 'intelligence' && (
          <section className="bg-white p-10 rounded-[3rem] border border-zinc-200 shadow-sm animate-in slide-in-from-right-4">
            <div className="flex items-center gap-4 mb-10">
              <div className="p-3 bg-amber-500 rounded-2xl text-white shadow-xl"><Sparkles size={24} /></div>
              <div>
                <h3 className="text-2xl font-black text-black tracking-tight uppercase">Artificial Intelligence</h3>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mt-1">External Brain Configuration</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="p-8 bg-zinc-50 rounded-[2.5rem] border border-zinc-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white rounded-xl shadow-sm border border-zinc-100"><Unlock size={16} className="text-zinc-400" /></div>
                  <h4 className="font-black text-sm uppercase tracking-tight">Gemini API Key</h4>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed font-bold mb-6 max-w-md">
                  Required for Project Intel features. The key is stored locally in your browser and is never sent to our servers.
                </p>
                <div className="flex gap-2">
                  <input
                    type="password"
                    placeholder="AIzaSy..."
                    className="flex-1 bg-white border border-zinc-200 rounded-xl px-4 py-3 text-xs font-bold outline-none focus:ring-2 focus:ring-black/10 transition-all font-mono"
                    defaultValue={localStorage.getItem('lc_api_key') || ''}
                    onChange={(e) => {
                      if (e.target.value.trim().length > 0) {
                        localStorage.setItem('lc_api_key', e.target.value.trim());
                      } else {
                        localStorage.removeItem('lc_api_key');
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      addToast({ type: 'success', title: 'Brain Connected', message: 'API Key updated locally.' });
                      setTimeout(() => window.location.reload(), 1000);
                    }}
                    className="bg-black text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-lg"
                  >
                    Save
                  </button>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${localStorage.getItem('lc_api_key') ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}></div>
                  <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">
                    {localStorage.getItem('lc_api_key') ? 'Connection Ready' : 'Key Missing'}
                  </span>
                </div>
              </div>

              <div className="p-8 rounded-[2.5rem] bg-indigo-50 border border-indigo-100">
                <h4 className="font-black text-sm uppercase tracking-tight text-indigo-900 mb-4 flex items-center gap-2">
                  <Rocket size={16} /> Capabilities Activated
                </h4>
                <ul className="space-y-3">
                  {[
                    "Automated RAG Analysis of Project Documents",
                    "Margin variance detection & alerts",
                    "Compliance risk scanning on PDF upload",
                    "Interactive schedule delay analysis"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-xs font-bold text-indigo-800">
                      <Check size={14} className="text-indigo-500" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )
      }
    </div >
  );
};
